/**
 * React Query hooks for Agent Hives
 *
 * Provides data fetching and mutations for hive management.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  Hive,
  HiveCreate,
  HiveUpdate,
  HiveGraph,
  HiveListResponse,
} from '../types';

const HIVES_KEY = 'hives';
const HIVE_GRAPH_KEY = 'hive-graph';

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch all hives for the current tenant
 */
export function useHives() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [HIVES_KEY, tenantId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<HiveListResponse>('/api/v1/hives');
      return data;
    },
    enabled: !!tenantId,
  });
}

/**
 * Fetch single hive by ID
 */
export function useHive(id: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [HIVES_KEY, tenantId, id],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      // ISS-406: the detail endpoint wraps the hive in the standard
      // {"data": ...} envelope (HiveDetailResponse / RestrictedHiveResponse).
      const { data } = await apiClient.get<{ data: Hive }>(`/api/v1/hives/${id}`);
      return data.data;
    },
    enabled: !!id && !!tenantId,
  });
}

/**
 * Fetch hive graph topology
 */
export function useHiveGraph(hiveId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [HIVE_GRAPH_KEY, tenantId, hiveId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<HiveGraph>(`/api/v1/hives/${hiveId}/graph`);
      return data;
    },
    enabled: !!hiveId && !!tenantId,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Create a new hive
 */
export function useCreateHive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hiveData: HiveCreate) => {
      const { data } = await apiClient.post<{ data: Hive }>('/api/v1/hives', hiveData);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
    },
  });
}

/**
 * Update an existing hive
 */
export function useUpdateHive(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hiveData: HiveUpdate) => {
      const { data } = await apiClient.patch<{ data: Hive }>(`/api/v1/hives/${id}`, hiveData);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
    },
  });
}

/**
 * Delete a hive
 */
export function useDeleteHive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/hives/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
    },
  });
}

/**
 * Add an agent to a hive
 */
export function useAddAgentToHive(hiveId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentData: { agent_id: string; is_entry?: boolean }) => {
      const { data } = await apiClient.post(`/api/v1/hives/${hiveId}/agents`, agentData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVE_GRAPH_KEY] });
    },
  });
}

/**
 * Remove an agent from a hive
 */
export function useRemoveAgentFromHive(hiveId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentId: string) => {
      await apiClient.delete(`/api/v1/hives/${hiveId}/agents/${agentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVE_GRAPH_KEY] });
    },
  });
}
